import { useAdminSession } from '@/lib/admin-session';
import { ApiError } from '@/lib/api';
import { prefillRefusedSentence } from '@/lib/npc-labels';
import { replayStartedLabel } from '@/lib/replay-mark';
import { shortReplayId } from '@/lib/replay-mark';
import { SESSION_DETAIL_KEY, fetchSessionDetail } from '@/lib/session-api';
import { sessionStatusLabel } from '@/lib/session-labels';
import type { NpcPrefillReason } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

/**
 * Where an NPC came from (npc.md §4.2, N14; maquette `DepuisSession`, planche 7).
 *
 * Four small pieces around the session an NPC was born from:
 *
 *   - `NpcOriginNote` - « Née de la session 7c1e4a90 (Le Train v24, 24 oct.) » under the kind, a
 *     link to the session; **« session supprimée »** when the session is gone, because an NPC does
 *     not follow its session (N14): the trash or the purge of the session leaves it whole, and the
 *     link says so rather than leading to « Aucune session sous cet identifiant »;
 *   - `NpcOriginCard` - « La session d'origine » in the right column of a draft prefilled from it:
 *     experience, date, status, turns, and the door to the session;
 *   - `NpcPrefillBanner` - « Brouillon : relisez, puis enregistrez la v1 »;
 *   - `NpcPrefillRefusal` - the refusal of the prefill, in French, with the way back.
 *
 * The first two read the session through the cache key the parcours and the conversation share
 * (`SESSION_DETAIL_KEY`): opening the session after the NPC costs nothing. A 404 is an ANSWER here -
 * « supprimée » - and is never retried.
 */

function useOriginSession(sessionId: string) {
  const { email } = useAdminSession();
  return useQuery({
    queryKey: SESSION_DETAIL_KEY(email, sessionId),
    queryFn: () => fetchSessionDetail(sessionId),
    retry: false,
    // A purge is not undone and a session does not move under a draft: one read is enough.
    staleTime: 60_000,
  });
}

function isGone(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function NpcOriginNote({ sessionId }: { sessionId: string }) {
  const session = useOriginSession(sessionId);
  if (isGone(session.error)) {
    return (
      <p
        data-testid="npc-origine"
        data-gone="true"
        className="text-caption text-gray-600 leading-4"
      >
        Née d’une session supprimée : le NPC ne la suit pas.
      </p>
    );
  }
  const row = session.data?.session;
  return (
    <p data-testid="npc-origine" className="text-caption text-gray-600 leading-4">
      Née de la session{' '}
      <Link
        to="/admin/sessions/$sessionId"
        params={{ sessionId }}
        data-testid="npc-origine-lien"
        className="text-bleu-nuit underline underline-offset-2"
      >
        {shortReplayId(sessionId)}
        {row === undefined || row.experienceName === null
          ? ''
          : ` (${row.experienceName}${
              row.experienceVersionNumber === null ? '' : ` v${row.experienceVersionNumber}`
            }, ${replayStartedLabel(row.startedAt).split(' à ')[0]})`}
      </Link>
      .
    </p>
  );
}

export function NpcOriginCard({ sessionId }: { sessionId: string }) {
  const session = useOriginSession(sessionId);
  const row = session.data?.session;
  return (
    <section
      data-testid="npc-origine-carte"
      className="space-y-2 rounded-md border-2 border-gray-200 bg-white p-5"
    >
      <h2 className="font-semibold text-bleu-nuit text-body-lg leading-7">La session d’origine</h2>
      {isGone(session.error) ? (
        <p className="text-gray-600 text-small">Cette session n’existe plus.</p>
      ) : row === undefined ? (
        <p className="text-gray-500 text-small italic">Lecture de la session…</p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-small">
          <dt className="text-gray-600">Expérience</dt>
          <dd className="font-semibold text-gray-900">
            {row.experienceName ?? '-'}
            {row.experienceVersionNumber === null ? '' : ` v${row.experienceVersionNumber}`}
          </dd>
          <dt className="text-gray-600">Jouée le</dt>
          <dd className="font-semibold text-gray-900">{replayStartedLabel(row.startedAt)}</dd>
          <dt className="text-gray-600">Statut</dt>
          <dd className="font-semibold text-gray-900">{sessionStatusLabel(row.status)}</dd>
          <dt className="text-gray-600">Tours</dt>
          <dd className="font-semibold text-gray-900">{row.turnCount}</dd>
        </dl>
      )}
      <Link
        to="/admin/sessions/$sessionId"
        params={{ sessionId }}
        data-testid="npc-origine-ouvrir"
        className="inline-block text-bleu-nuit text-small underline underline-offset-2"
      >
        Ouvrir la session
      </Link>
      <p className="border-gray-100 border-t pt-2 text-caption text-gray-600 leading-4">
        Ce qui n’entre pas dans la fiche : l’email du billet, le noyau retenu par la session, sa
        matière rangée. La source, ce sont ses mots, rien d’autre.
      </p>
    </section>
  );
}

export function NpcPrefillBanner({ sessionId }: { sessionId: string }) {
  return (
    // The alert's own icon stands where the pencil of the mockup stood: the package `Alert`
    // draws one icon per tone and takes no other.
    <div data-testid="npc-brouillon-bandeau">
      <Alert tone="warning">
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            <strong className="font-semibold">Brouillon</strong> : relisez, puis enregistrez la v1.
            Rien n’est créé tant que la v1 n’est pas enregistrée.
          </span>
          <span className="text-caption text-gray-700">
            pré-rempli depuis la session {shortReplayId(sessionId)}
          </span>
          <Link
            to="/admin/npc"
            data-testid="npc-brouillon-abandonner"
            className="ml-auto text-bleu-nuit text-small underline underline-offset-2"
          >
            Abandonner ce brouillon
          </Link>
        </span>
      </Alert>
    </div>
  );
}

/**
 * The prefill was refused or failed: the reason in French when the server gave one (`reason` of the
 * 409 `prefill-refused`), a generic line otherwise, and the way back to the session and to the
 * library. Nothing is created.
 */
export function NpcPrefillRefusal({
  reason,
  sessionId,
  gone,
}: {
  reason: NpcPrefillReason | null;
  sessionId: string;
  /** The session does not exist (a 404): nothing to link back to. */
  gone: boolean;
}) {
  return (
    <div data-testid="npc-prefill-refuse">
      <Alert tone="danger" title="Cette session ne peut pas donner un NPC.">
        <span className="block text-small">
          {gone
            ? 'Elle n’existe pas, ou le rejeu n’est pas ouvert sur ce serveur.'
            : reason === null
              ? 'Le brouillon n’a pas pu être préparé. Réessayez dans un instant.'
              : prefillRefusedSentence(reason)}
        </span>
        <span className="mt-2 flex flex-wrap gap-4 text-small">
          {gone ? null : (
            <Link
              to="/admin/sessions/$sessionId"
              params={{ sessionId }}
              className="text-bleu-nuit underline underline-offset-2"
            >
              Revenir à la session
            </Link>
          )}
          <Link to="/admin/npc" className="text-bleu-nuit underline underline-offset-2">
            Revenir aux NPC
          </Link>
        </span>
      </Alert>
    </div>
  );
}
